"use client";

import React, { useState } from "react";
import {
  canDirectlyShareToSocial,
  getDisplayShortLink,
} from "@/app/lib/shareLinks";

async function dataUrlToFile(dataUrl: string, filename: string) {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
}

async function urlToFile(url: string, filename: string) {
  const absoluteUrl =
    typeof window !== "undefined" && url.startsWith("/")
      ? `${window.location.origin}${url}`
      : url;

  const response = await fetch(absoluteUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
}

export default function SharePanel({
  title,
  shortPath,
  imageSrc,
}: {
  title: string;
  shortPath: string;
  imageSrc?: string;
}) {
  const [statusText, setStatusText] = useState("");

  const shareLink = getDisplayShortLink(shortPath);

  async function copyShortLink() {
    await navigator.clipboard.writeText(shareLink);
    setStatusText("Private wedding link copied.");
  }

  async function shareNative() {
    try {
      if (imageSrc && navigator.share) {
        const filename = `${title.toLowerCase().replace(/\s+/g, "-")}.png`;
        const file = imageSrc.startsWith("data:")
          ? await dataUrlToFile(imageSrc, filename)
          : await urlToFile(imageSrc, filename);

        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            title,
            text: shareLink,
            files: [file],
          });
          setStatusText("Shared using your device.");
          return;
        }
      }

      if (navigator.share) {
        await navigator.share(
          canDirectlyShareToSocial()
            ? {
                title,
                text: shareLink,
                url: shareLink,
              }
            : {
                title,
                text: shareLink,
              }
        );
        setStatusText("Shared using your device.");
        return;
      }

      await copyShortLink();
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        console.error(error);
        setStatusText("Unable to open the device share sheet right now.");
      }
    }
  }

  async function shareInstagram() {
    await copyShortLink();
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    setStatusText("Private wedding link copied for Instagram.");
  }

  async function shareFacebook() {
    if (canDirectlyShareToSocial()) {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`,
        "_blank",
        "noopener,noreferrer"
      );
      setStatusText("Facebook share opened.");
      return;
    }

    await copyShortLink();
    setStatusText("Private wedding link copied.");
  }

  return (
    <div className="mt-4 rounded-[1.25rem] border border-[#ead8a0] bg-[#fcfbf8] p-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-[11px] uppercase tracking-[.18em] text-[#8c8c86]">
            Share on socials
          </p>
          <p className="truncate text-sm font-medium text-[#1f3f6d]">
            Private wedding link
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <button
            type="button"
            onClick={copyShortLink}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-sm font-semibold text-[#1f4e79] transition hover:bg-[#f2ecdf]"
          >
            Copy Link
          </button>

          <button
            type="button"
            onClick={shareFacebook}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-sm font-semibold text-[#1f4e79] transition hover:bg-[#f2ecdf]"
          >
            Facebook
          </button>

          <button
            type="button"
            onClick={shareInstagram}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-sm font-semibold text-[#1f4e79] transition hover:bg-[#f2ecdf]"
          >
            Insta
          </button>

          <button
            type="button"
            onClick={shareNative}
            className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#1f4e79] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#173d61]"
          >
            Share
          </button>
        </div>

        {statusText ? (
          <p className="text-sm leading-6 text-[#686861]">{statusText}</p>
        ) : null}
      </div>
    </div>
  );
}
